
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';

export default function CreateInvite() {
  const navigate = useNavigate();
  const [className, setClassName] = useState('');
  const [studentEmails, setStudentEmails] = useState('');
  const [inviteLinks, setInviteLinks] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const generateInviteLinks = () => {
    setLoading(true);
    
    const emails = studentEmails.split('\n').filter(email => email.trim());
    const links = emails.map(email => {
      const inviteCode = Math.random().toString(36).substring(2, 15);
      return `${window.location.origin}/auth/login?invite=${inviteCode}&email=${encodeURIComponent(email.trim())}`;
    });
    
    setTimeout(() => {
      setInviteLinks(links);
      setLoading(false);
    }, 1000);
  };

  const copyAllLinks = async () => {
    const linksText = inviteLinks.join('\n');
    try {
      await navigator.clipboard.writeText(linksText);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = linksText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4">
      <div className="max-w-4xl mx-auto pt-8">
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#2d5a35] hover:bg-green-50 px-3 py-2 rounded-lg transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            <span>Back to Home</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-[#2d5a35]">Create Student Invites</h1>
            <p className="text-gray-600 mt-2">Generate invite links for your students</p>
          </div>
          
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Create Invites Form */}
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-xl flex items-center justify-center">
                <i className="ri-user-add-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Invite Students</h3>
                <p className="text-gray-600">Create personalized invite links</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class Name
                </label>
                <input
                  type="text"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5a35] focus:border-transparent"
                  placeholder="e.g., Grade 2 Reading Class"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student Emails (one per line)
                </label>
                <textarea
                  value={studentEmails}
                  onChange={(e) => setStudentEmails(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5a35] focus:border-transparent h-40"
                  placeholder={`student1@school.edu\nstudent2@school.edu\nstudent3@school.edu`}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Enter one email address per line
                </p>
              </div>

              <Button
                onClick={generateInviteLinks}
                disabled={!className.trim() || !studentEmails.trim() || loading}
                className="w-full py-3"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Generating Links...
                  </>
                ) : (
                  <>
                    <i className="ri-links-line mr-2"></i>
                    Generate Invite Links
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Generated Links */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <i className="ri-links-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Invite Links</h3>
                  <p className="text-gray-600">Share these with your students</p>
                </div>
              </div>
              
              {inviteLinks.length > 0 && (
                <Button
                  onClick={copyAllLinks}
                  variant="outline"
                  className="text-sm"
                >
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy All
                </Button>
              )}
            </div>

            {inviteLinks.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-links-line text-gray-300 text-6xl mb-4"></i>
                <p className="text-gray-500">No invite links generated yet</p>
                <p className="text-sm text-gray-400 mt-2">
                  Fill out the form and click "Generate Invite Links"
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {inviteLinks.map((link, index) => {
                  const email = studentEmails.split('\n')[index]?.trim();
                  return (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 text-sm mb-1">
                            {email}
                          </p>
                          <p className="text-xs text-gray-500 break-all font-mono">
                            {link}
                          </p>
                        </div>
                        <button
                          onClick={() => navigator.clipboard.writeText(link)}
                          className="ml-2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#2d5a35] hover:bg-white rounded-lg transition-colors flex-shrink-0"
                        >
                          <i className="ri-file-copy-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Instructions */}
        <Card className="mt-8 p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-3">How to Use Invite Links</h3>
              <div className="space-y-2 text-gray-600">
                <p className="flex items-start space-x-2">
                  <span className="text-[#2d5a35] font-bold">1.</span>
                  <span>Copy the invite links and send them to your students via email or your school's communication system</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="text-[#2d5a35] font-bold">2.</span>
                  <span>Students click the link and will be taken directly to the sign-up page</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="text-[#2d5a35] font-bold">3.</span>
                  <span>They'll be automatically enrolled in your class after creating their account</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="text-[#2d5a35] font-bold">4.</span>
                  <span>Students without invite links will need to create regular accounts and won't be enrolled in any class</span>
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
